import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { fetchProfessionalProfile } from '~/actions';
import { Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import {
  BackButton,
  Box,
  CardSpinner,
  Column,
  FloatingContainer,
  Row,
  Screen,
  ZStack,
} from '~/commonComponents';
import {
  CandidateStatus,
  CandidateStatusMapper,
  useGetCandidateOnsiteInterviewList,
  useGetCandidateProfessional,
} from '~/domain/PermanentJobs';
import { useAppSelector } from '~/hooks';
import {
  CheckCircleOutlineIcon,
  CircleCheckSolidIcon,
  CloseCircleOutlineIcon,
} from '~/images/icons';
import { HirePopupConfirmation } from '../PermanentJobsV2/JobOverview/components/CandidateTabView/CandidateListTable/HirePopup';
import { RejectPopupConfirmation } from '../PermanentJobsV2/JobOverview/components/CandidateTabView/CandidateListTable/RejectPopup';
import { ProfessionalProfile } from './components';
import { ExperienceOverview, OnsiteInterviewsPanel } from './containers';
import { useHireCandidate } from './hooks/useHireCandidate';
import { useRejectCandidate } from './hooks/useRejectCandidate';
import { TCandidateOverviewPageRoutes } from './Types';

export const CandidateOverview = () => {
  const dispatch = useDispatch();

  const { jobPostingId, professionalId } = useParams<TCandidateOverviewPageRoutes>();

  const professional = useAppSelector((state) => state.user.professionalProfile);

  const { data: candidate, isInitialLoading: isGetCandidateProfessionalLoading } =
    useGetCandidateProfessional(
      {
        postingId: jobPostingId || '',
        professionalId: professionalId || '',
      },
      { enabled: !!jobPostingId && !!professionalId },
    );

  const { data, isInitialLoading: isGetCandidateOnsiteInterviewListLoading } =
    useGetCandidateOnsiteInterviewList(
      {
        postingId: jobPostingId || '',
        professionalId: professionalId || '',
      },
      {
        enabled: !!jobPostingId && !!professionalId,
      },
    );

  const {
    canHire,
    controls: controlsHire,
    isOpen: isHireOpen,
    handleHireClick,
  } = useHireCandidate({
    candidate,
    hasCompletedInterview: data?.hasCompletedInterviews,
  });

  const {
    canReject,
    controls: controlsReject,
    isOpen: isRejectOpen,
    handleRejectClick,
  } = useRejectCandidate({ candidate });

  const hasWorkedWith = useMemo(() => {
    const obj = {
      dso: false,
      office: false,
    };

    data?.groups.action_needed.forEach((id) => {
      const it = data.maps[id];

      if (it.counterOffer?.hasWorkedWithDSO) {
        obj.dso = true;
      }

      if (it.counterOffer?.hasWorkedWithOffice) {
        obj.office = true;
      }
    });

    return obj;
  }, [data?.groups.action_needed, data?.maps]);

  useEffect(() => {
    if (professionalId) dispatch(fetchProfessionalProfile(professionalId));
  }, [dispatch, professionalId]);

  if (
    !professional ||
    isGetCandidateProfessionalLoading ||
    isGetCandidateOnsiteInterviewListLoading
  ) {
    return <CardSpinner />;
  }

  return (
    <>
      <Screen>
        <FloatingContainer borderRadius={0} flex={1} paddingBlock={54} paddingInline={48}>
          <Row position="relative" alignItems="center" justifyContent="center">
            <ZStack left={0}>
              <BackButton />
            </ZStack>
            <Column>
              <Typography as="span" bold size="heading_2" textAlign="center">
                Candidate Overview
              </Typography>
              {data?.officeStatus && (
                <Typography
                  as="span"
                  bold
                  size="body_large"
                  style={{
                    alignItems: 'center',
                    display: 'inline-flex',
                    gap: 4,
                    justifyContent: 'center',
                  }}
                >
                  Status:{' '}
                  <Typography
                    as="span"
                    color={CandidateStatusMapper.getColor(data.officeStatus)}
                    size="body_large"
                  >
                    {CandidateStatusMapper.toHumanReadable(data.officeStatus).toUpperCase()}
                  </Typography>
                  {data.officeStatus === CandidateStatus.Hired && <CircleCheckSolidIcon />}
                </Typography>
              )}
            </Column>
          </Row>

          <Row gap={20} marginTop={48}>
            <Box flex={1}>
              <ProfessionalProfile
                disableNavigation
                hasWorkedWithDSO={hasWorkedWith.dso}
                hasWorkedWithOffice={hasWorkedWith.office}
                professional={professional}
              />
            </Box>
            <Row flex={1} alignItems="center" justifyContent="center" gap={16}>
              <button
                className="shadow-xs hover__shadow-sm"
                data-cy="candidate-action-reject"
                disabled={!canReject}
                type="button"
                style={{
                  alignItems: 'center',
                  background: colors.neutral[100],
                  borderRadius: 48,
                  cursor: canReject ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  gap: 6,
                  height: 48,
                  justifyContent: 'center',
                  opacity: canReject ? 1 : 0.5,
                  paddingBlock: 14,
                  paddingInline: 16,
                  width: 140,
                }}
                onClick={handleRejectClick}
              >
                <CloseCircleOutlineIcon color={colors.primary[500]} />
                <Typography as="span" bold color={colors.primary[500]} size="body_large">
                  Reject
                </Typography>
              </button>
              <button
                className="shadow-xs hover__shadow-sm"
                data-cy="candidate-action-hire"
                disabled={!canHire}
                type="button"
                style={{
                  alignItems: 'center',
                  background: colors.primary[500],
                  borderRadius: 48,
                  cursor: canHire ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  gap: 6,
                  height: 48,
                  justifyContent: 'center',
                  opacity: canHire ? 1 : 0.5,
                  paddingBlock: 14,
                  paddingInline: 16,
                  width: 140,
                }}
                onClick={handleHireClick}
              >
                <CheckCircleOutlineIcon color={colors.shades[0]} />
                <Typography as="span" bold color={colors.shades[0]} size="body_large">
                  Hire
                </Typography>
              </button>
            </Row>
          </Row>

          <Row gap={20} marginTop={18}>
            <Column flex={1}>
              <ExperienceOverview
                hasWorkedWithDSO={hasWorkedWith.dso}
                hasWorkedWithOffice={hasWorkedWith.office}
                postingId={jobPostingId}
                professional={professional}
              />
            </Column>
            <Column flex={1}>
              {data && (
                <OnsiteInterviewsPanel
                  postingId={jobPostingId ?? ''}
                  groups={data.groups}
                  maps={data.maps}
                  officeId={candidate?.officeId || ''}
                  officeStatus={data.officeStatus}
                  professionalId={professional.id}
                  professionalName={professional.full_name || ''}
                />
              )}
            </Column>
          </Row>
        </FloatingContainer>
      </Screen>

      {isHireOpen && !!candidate && (
        <HirePopupConfirmation
          candidateId={candidate.id}
          controls={controlsHire}
          professionalName={professional.full_name}
        />
      )}

      {isRejectOpen && !!candidate && (
        <RejectPopupConfirmation
          candidateId={candidate.id}
          controls={controlsReject}
          hasScheduledInterview={data?.hasScheduledInterview}
          professionalName={professional.full_name}
        />
      )}
    </>
  );
};
